import idx from 'idx';

import { NoNetworkError } from '@confluence/network';
import { isUnauthorizedError } from '@confluence/error-boundary';
import { getApolloClient, markErrorAsHandled } from '@confluence/graphql';
import { getAnalyticsWebClient } from '@confluence/analytics-web-client';

import { QuickReloadQuery } from './QuickReloadQuery.experimentalgraphql';
import { EditsStream, CommentsStream } from './QuickReloadStreams';
import { PageMode } from './pubSubConstants';
/**
 * This poller is a service provided to the <QuickReload> to poll the GraphQL
 * server for updates, based on the timing of a timer class (supplied as a
 * parameter). It guards against the responses coming out of sequence, or
 * being late enough that the host has changed state and are thus irrelevant.
 *
 * Note: the logic has its flaws...
 *
 * 1) If you start the poller with the lastPollTime set to a Date.now(), then
 *    if any changes happen between the page content being requested and the
 *    timer initializing, we won't be able to report them. So use the time of
 *    the document's most recent edit, or most recent known comment (whichever
 *    is later) for lastPollTime.
 *
 * 2) While the query response tells us the whole list of comments that have
 *    come in since the 'since' time, it doesn't give us a corresponding list of
 *    edits: we only get to know about the most recent edit. If multiple
 *    changes have taken place in that time, we'll misreport them as a single
 *    edit.
 */

export async function queryAndIngest(
	query,
	since,
	contentId,
	querySeq,
	pollerState,
	pageMode: PageMode,
): Promise<number | undefined> {
	let response;
	if (!contentId || !since) {
		return;
	}

	try {
		response = await query({
			query: QuickReloadQuery,
			variables: {
				contentId,
				since,
			},
			fetchPolicy: 'no-cache',
			errorPolicy: 'all',
			context: {
				fetchOptions: {
					ignoreNoNetworkError: true,
				},
				single: true,
			},
		});
	} catch (e) {
		if (
			isUnauthorizedError(e) ||
			e instanceof NoNetworkError ||
			e.message?.includes('Failed to fetch')
		) {
			markErrorAsHandled(e);
		}
		/**
		 * Don't do anything. By virtue of which, we're not returning a timestamp,
		 * which means the poller won't advance its 'since' time, and will re-request
		 * from the same start time.
		 */
		return;
	}
	if (!response.errors || response.errors.length === 0) {
		pollerState.consecutiveFailures = 0;
	}

	if (response.errors && response.errors.length) {
		const status = idx(response, (_) => _.data.experimentalQuickReload.status);

		// Check for 429 status code and increment the counter
		if (status === 429) {
			pollerState.consecutiveFailures += 1;

			// If 3 consecutive 429 status code failures, stop the poller
			if (pollerState.consecutiveFailures >= 3) {
				pollerState.stopped = true;
				void getAnalyticsWebClient().then((client) => {
					client.sendOperationalEvent({
						source: 'quick-reload',
						action: 'pollerStopped',
						actionSubject: 'poller',
					});
				});
				return;
			}
		} else {
			// Reset the counter if the status code is not 429
			pollerState.consecutiveFailures = 0;
		}

		const unAuthorizedErrors = response.errors.filter((e) => isUnauthorizedError(e));
		const networkErrors = response.errors.filter((e) => e instanceof NoNetworkError || !status);
		// handle unauthorized,no network and network failure errors
		[...unAuthorizedErrors, ...networkErrors].forEach((e) => markErrorAsHandled(e));
		/**
		 * Same as the don't-do-anything situation above
		 */
		return;
	}

	/**
	 * If, while awaiting the query's resolution, the poller got shut down (maybe
	 * the <QuickReload>'s contentId prop changed), do nothing. If we proceeded
	 * to add the event, we could be displaying a flag that relates to a
	 * different page.
	 */
	if (pollerState.stopped) return;

	/**
	 * If the poller has issued a new query while this one was pending, discard
	 * the result. The more recent query will be asking from the same 'since'
	 * date as this one (since this one didn't complete yet to bump it up),
	 * so if we didn't discard it we'd be double-counting.
	 * (That said, edits are different from comments, and...)
	 */
	if (querySeq !== pollerState.querySeq) return;

	/**
	 * Show a flag for any new comments that came in
	 */
	const comments = idx(response, (_) => _.data.experimentalQuickReload.comments) || [];

	const commentEvents = comments.map((comment) => ({
		id: idx(comment, (_) => _.comment.id),
		displayName: idx(comment, (_) => _.comment.author.displayName),
		url: idx(comment, (_) => _.comment.url),
		accountId: idx(comment, (_) => _.comment.author.accountId),
		profilePicturePath: idx(comment, (_) => _.comment.author.profilePicture.path),
		markerRef: comment?.markerRef,
	}));

	if (commentEvents.length) pollerState.commentsStream.addEvents(commentEvents);

	/**
	 * Show a flag for any new edits that came in
	 * Note that the query can't represent multiple edits happening between
	 * polls: it reports only who most recently edited it, or null if no edits
	 * happened between polls.
	 */
	const editorForPage = idx(response, (_) => _.data.experimentalQuickReload.editorForPage);

	if (editorForPage && pageMode === PageMode.RENDERER) {
		const displayName = idx(editorForPage, (_) => _.displayName) || '';
		const accountId = idx(editorForPage, (_) => _.accountId) || null;
		const profilePicturePath = idx(editorForPage, (_) => _.profilePicture.path) || null;
		pollerState.editsStream.addEvents([{ displayName, accountId, profilePicturePath }]);
	}

	/**
	 * Return the timestamp that the server said was current at the time of
	 * the poll so we can use this for the next 'since' time.
	 */
	return idx(response, (_) => _.data.experimentalQuickReload.time);
}

async function timerDidFire(
	since,
	contentId,
	querySeq,
	pollerState,
	pageMode: PageMode,
): Promise<number | undefined> {
	if (pollerState.stopped) return;
	return queryAndIngest(getApolloClient().query, since, contentId, querySeq, pollerState, pageMode);
}

export function startQuickReloadPoller(
	intl,
	Timer,
	contentId,
	contentType,
	currentAccountId,
	lastPollTime,
	makeShowFlag,
	hideFlags,
	setLastNotificationReceivedTime,
	pageMode: PageMode,
	fetchOnStart,
) {
	const state = {
		querySeq: 0,
		stopped: false,
		consecutiveFailures: 0, // This is added for disaster recovery sistuations to stop the poller after 3 consecutive failures so we don't keep polling
		editsStream: new EditsStream(intl, currentAccountId, contentType, makeShowFlag('edit')),
		commentsStream: new CommentsStream(
			intl,
			currentAccountId,
			contentType,
			makeShowFlag('comment'),
		),
	};

	const timer = new Timer(async () => {
		const thisPollTime = await timerDidFire(
			lastPollTime,
			contentId,
			++state.querySeq,
			state,
			pageMode,
		);

		// lastPollTime is actually tracking the last _successful_ poll time
		if (thisPollTime) {
			lastPollTime = thisPollTime;
			setLastNotificationReceivedTime(thisPollTime);
		}
	});

	timer.start(fetchOnStart);

	// Return a function to stop the poller
	return (shouldHideFlags: boolean = true) => {
		state.stopped = true;
		timer.stop();

		if (shouldHideFlags) {
			hideFlags();
		}
	};
}
